import { loadServicesForObject } from "src/businessServices/apiBusinessServices";
import { ObjectServiceExt } from "src/businessServices/businessServises.types";
import { onError } from "src/common/onError";
import { ZAttribute } from "src/types/ZAttribute";
import { ZObjState } from "src/types/ZObjState";
import { ZUid } from "src/types/zUid";
import { loadObjectStates } from "../../Obj2Tab/roles/rolesApi";
import { loadObjectAttrinbutesAll } from "../../objectsApi";
import { CacheGuard } from "./cacheGuard";
import { unsavedId } from "./node";
import { Resource } from "./resource";
import { checkStates } from "./states";

/**
 * Сопутствующие данные объекта: подключённые бизнес-сервисы, полный список
 * атрибутов и состояния.
 *
 * Это не часть ObjectDto и не данные формы. Сервисы задают вкладки на экране
 * объекта, полный список атрибутов нужен формам и копированию, а состояния —
 * и формам, и дереву: по ним строится таблица прав атрибута.
 *
 * Состояния объекта меняются только через API состояний и workflow, поэтому
 * держать их по объекту безопасно: правки атрибутов и групп их не касаются.
 */
export class ObjectServicesStore {
  private readonly guard: CacheGuard;

  constructor(cacheKey: () => string) {
    this.guard = new CacheGuard(cacheKey);
  }

  private readonly services = new Resource<ZUid, ObjectServiceExt[]>(
    (objectId) => loadServicesForObject(objectId),
    String,
  );

  private readonly attributes = new Resource<ZUid, ZAttribute[]>(
    (objectId) => loadObjectAttrinbutesAll(objectId),
    String,
  );

  private readonly objectStates = new Resource<ZUid, ZObjState[]>(
    async (objectId) => checkStates(await loadObjectStates(objectId)),
    String,
  );

  /** У несохранённого объекта запрашивать нечего. */
  async ensure(objectId: ZUid) {
    if (objectId === unsavedId) return;
    // Сменился пользователь, язык или версия модели - прежние данные не годятся
    if (this.guard.isStale(objectId)) this.forget(objectId);
    await Promise.all([
      this.services.get(objectId),
      this.attributes.get(objectId),
      // Без состояний форма покажет меньше, но объект остаётся работоспособным:
      // отказ не должен рушить его загрузку целиком
      this.objectStates.get(objectId).catch(onError),
    ]);
  }

  statesOf(objectId: ZUid): ZObjState[] | undefined {
    const state = this.objectStates.state(objectId);
    return state.status === "ready" ? state.value : undefined;
  }

  servicesOf(objectId: ZUid): ObjectServiceExt[] | undefined {
    const state = this.services.state(objectId);
    return state.status === "ready" ? state.value : undefined;
  }

  attributesOf(objectId: ZUid): ZAttribute[] | undefined {
    const state = this.attributes.state(objectId);
    return state.status === "ready" ? state.value : undefined;
  }

  /** Список сервисов изменился: подключение, отключение, переименование. */
  async reloadServices(objectId: ZUid) {
    this.services.invalidate(objectId);
    if (objectId !== unsavedId) await this.services.get(objectId);
  }

  /** Перечитать после записи: структура объекта могла измениться. */
  async reload(objectId: ZUid) {
    if (objectId === unsavedId) return;
    this.attributes.invalidate(objectId);
    this.objectStates.invalidate(objectId);
    await Promise.all([
      this.attributes.get(objectId),
      this.reloadServices(objectId),
      this.objectStates.get(objectId).catch(onError),
    ]);
    this.guard.remember(objectId);
  }

  clear() {
    this.services.clear();
    this.attributes.clear();
    this.objectStates.clear();
    this.guard.clear();
  }

  forget(objectId: ZUid) {
    this.services.invalidate(objectId);
    this.attributes.invalidate(objectId);
    this.objectStates.invalidate(objectId);
    this.guard.forget(objectId);
  }
}
